import { useState, useRef, useMemo, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Divider,
    Grid2 as Grid,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { useLocation, useParams, Link, useHistory } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PageContainer from '../Components/PageContainer';
import { COLORS, DATE_FORMATS } from '../../utils/Application_Constants';
import RichTextEditor from '../../Shared/Components/RichTextEditor';
import ActivityFeed from '../Components/ActivityFeed/ActivityFeed';
import Attachments from '../../components/ContentComponents/ProcessedTimesheetOverview/Attachments';
import ReviewReasonsAccordion from '../Components/ReviewReasons';
import ProcessedTimesheetHeader from '../Components/ProcessedTimesheets/ProcessedTimesheetHeader';
import StripedDataGrid from '../../Shared/Components/Table/StripedDataGrid';
import useGetProcessedTimesheetDetails from '../hooks/useGetProcessedTimesheetDetails';
import useGetActivityFeed from '../hooks/useGetActivityFeed';
import usePostActivityFeedComment from '../hooks/usePostActivityFeedComment';
import { pluralize, currencyFormatter } from '../../utils/helpers';
import formatDate, { formatUtcDate } from '../../utils/formatDate';
import { TimesheetStatusEnumApi } from '../../components/ContentComponents/Timesheets/Status.enum';
import {
    fillEmptyProcessedTimesheetEntriesValues,
    processedTimesheetEntriesTableColumns,
    formatRowsByRelatedEntryId,
} from '../../Shared/Utils/processedTimesheetDetailsTableColumns';
import { formatIntegerOrDecimal } from '../../utils/formatIntegerOrDecimal';
import DownloadTimesheetsButton from '../Components/DownloadTimesheets/DownloadTimesheetsButton';
import { config as AdminTableConfig } from '../Configurations/TimesheetReviewConfig';
import ProcessedTimesheetDetailsTable from '../Components/ProcessedTimesheets/ProcessedTimesheetDetailsTable';
import PaperTimesheetBadge from '../../Shared/Components/PaperTimesheetBadge';
import CorrectionsBadge from '../../Shared/Components/CorrectionsBadge';
import {
    canEditAttachments,
    canEditHeaderDates,
    isPtsReadOnly,
} from '../Configurations/processedTimesheetsConfig';
import {
    ProcessedTimesheetContextProvider,
    useProcessedTimesheetContext,
} from '../Contexts/ProcessedTimesheetContext';
import CorrectionCard from '../Components/ProcessedTimesheets/CorrectionCard';
import AssociatedInvoiceDocuments from '../Components/ProcessedTimesheets/AssociatedInvoiceDocuments';
import ProcessedTimesheetHistory from '../Components/ProcessedTimesheets/ProcessedTimesheetHistory';
import PartialInvoiceBanner from '../Components/ProcessedTimesheets/PartialInvoiceBanner';
import LocationMismatchBanner from '../Components/ProcessedTimesheets/LocationMismatchBanner';
import { useToast } from '../Contexts/ToastContext';
import useAdminAuthContext from '../hooks/useAdminAuthContext';
import useRedirectToAggregatePts from '../hooks/useRedirectToAggregatePts';
import { formatTimesheetApproverLabel } from '../Utils/formatTimesheetApproverLabel';

const EMPTY_ARRAY = [];

const styles = () => ({
    box: {
        backgroundColor: COLORS.WHITE,
        borderRadius: 10,
        border: `1px solid ${COLORS.LT_MIDNIGHT10}`,
        padding: '24px',
    },
    noPadding: { padding: 0 },
    totalContainer: { padding: 8, boxSizing: 'border-box' },
    total: {
        alignSelf: 'center',
        fontSize: 14,
        lineHeight: '22px',
        color: COLORS.LT_MIDNIGHT,
        fontWeight: 700,
        '& span': {
            marginLeft: 4,
            fontWeight: 400,
        },
    },
    timeEntryHeaderMetrics: {
        fontSize: 12,
        lineHeight: '20px',
        color: COLORS.LT_MIDNIGHT75,
    },
    timeEntryMetrics: {
        fontSize: 14,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 4,

        '& span': {
            fontSize: 9,
            textTransform: 'uppercase',
        },
    },
});

const renderPtsLink = (processedTimesheetId, text, target = '_blank') => (
    <Link
        to={`/processed-timesheets/${processedTimesheetId}`}
        style={{ textDecoration: 'underline', color: COLORS.LT_JOURNEY }}
        target={target}
    >
        {text}
    </Link>
);

const ProcessedTimesheetDetails = ({ classes }) => {
    const { id: processedTimesheetId } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const followToAggregate = queryParams.get('followToAggregate') === 'true';
    const { replace } = useHistory();
    const { showSuccessToast } = useToast();
    const {
        data: timesheetDetailsData,
        isLoading: timesheetDetailsLoading,
        isError: errorLoadingDetails,
        isFetching: timesheetDetailsFetching,
        refetch: refetchProcessedTimesheetDetails,
    } = useGetProcessedTimesheetDetails({ processedTimesheetId, followToAggregate });

    useRedirectToAggregatePts({
        processedTimesheetId,
        timesheetDetailsData,
        isLoading: timesheetDetailsLoading,
        isFetching: timesheetDetailsFetching,
    });

    const [postCommentError, setPostCommentError] = useState(null);
    const rteRef = useRef(null);
    const { isLTAssociateUser, isTimesheetSystemAdminUser } = useAdminAuthContext();
    const { hasRecentChanges } = useProcessedTimesheetContext();
    const { ltoeApiTimesheetEnableEditApprovedTimesheet } = useFlags();

    const clinicianId = timesheetDetailsData?.clinicianId ?? null;
    const clinicianName = timesheetDetailsData?.clinicianName ?? null;
    const rawTimesheetId = timesheetDetailsData?.rawTimesheetId ?? null;
    const isEditionDisabled =
        hasRecentChanges ||
        timesheetDetailsLoading ||
        errorLoadingDetails ||
        !ltoeApiTimesheetEnableEditApprovedTimesheet;
    const readOnly = isPtsReadOnly(timesheetDetailsData?.status) || isEditionDisabled;
    const canEditAttachment =
        canEditAttachments(timesheetDetailsData?.status) && !isEditionDisabled;
    const canEditHeaderDate =
        canEditHeaderDates(timesheetDetailsData?.status) && !isEditionDisabled;

    const canEditRawTimesheet =
        [
            TimesheetStatusEnumApi.Approved,
            TimesheetStatusEnumApi.TimeNotEntered,
            TimesheetStatusEnumApi.DidNotWork,
            TimesheetStatusEnumApi.Rejected,
        ].includes(timesheetDetailsData?.rawTimesheetStatus) &&
        !(timesheetDetailsData?.oldVersion || timesheetDetailsData?.status === 'Outdated');

    const {
        data: activityFeedData,
        isFetching: activityFeedLoading,
        isError: isActivityFeedError,
        refetch: refetchActivityFeed,
    } = useGetActivityFeed({ processedTimesheetId });

    const { mutate: postActivityFeedComment, isLoading: isPostingComment } =
        usePostActivityFeedComment({
            onMutate: () => {
                setPostCommentError(null);
            },
            onSuccess: () => {
                refetchActivityFeed();
                rteRef.current?.reset();
            },
            onError: (error) => {
                console.error('usePostActivityFeed:', error);
                setPostCommentError('Your comment could not be posted. Please try again.');
            },
        });

    const handleNewComment = (newComment) => {
        postActivityFeedComment({
            objectId: processedTimesheetId,
            formattedCommentContent: newComment,
            type: 'Comment',
        });
    };

    const showCorrectionCard =
        ltoeApiTimesheetEnableEditApprovedTimesheet &&
        isTimesheetSystemAdminUser &&
        ['InvoiceReview', 'Posted', 'Invoiced'].includes(timesheetDetailsData?.status);

    // If navigated here with a success flag in the query string, show a toast once
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const show = params.get('showCorrectionsSuccessToast') === 'true';
        if (show) {
            showSuccessToast(
                'New Processed Timesheet created successfully. Previous version marked as outdated.'
            );

            // remove the toast-related params so the toast doesn't show again on refresh/back
            params.delete('showCorrectionsSuccessToast');
            const newSearch = params.toString();
            try {
                replace(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
            } catch (err) {
                // ignore
            }
        }
    }, [location, showSuccessToast, replace]);

    const renderVersionAlert = () => {
        if (!timesheetDetailsData) return;
        if (timesheetDetailsData.oldVersion) {
            let actions;
            if (
                timesheetDetailsData.origProcessedTimesheetId &&
                timesheetDetailsData.origProcessedTimesheetId !== timesheetDetailsData.id
            ) {
                actions = (
                    <>
                        View the{' '}
                        {timesheetDetailsData.successorProcessedTimesheetId && (
                            <>
                                {renderPtsLink(
                                    timesheetDetailsData.successorProcessedTimesheetId,
                                    'newer version'
                                )}{' '}
                                or the{' '}
                            </>
                        )}
                        {renderPtsLink(
                            timesheetDetailsData.origProcessedTimesheetId,
                            'older version'
                        )}
                        .
                    </>
                );
            } else if (timesheetDetailsData.successorProcessedTimesheetId) {
                actions = (
                    <>
                        To see the newer version,{' '}
                        {renderPtsLink(
                            timesheetDetailsData.successorProcessedTimesheetId,
                            'click here'
                        )}
                        .
                    </>
                );
            }
            return (
                <Alert severity="warning">
                    You are viewing an outdated version of this processed timesheet. Some
                    functionalities may be disabled. {actions}
                </Alert>
            );
        }
        if (timesheetDetailsData.origProcessedTimesheetId) {
            return (
                <Alert severity="info">
                    You are viewing a newer version of this processed timesheet. To see the older
                    version{' '}
                    {renderPtsLink(timesheetDetailsData.origProcessedTimesheetId, 'click here')}.
                </Alert>
            );
        }
    };

    const locations = useMemo(() => {
        if (!timesheetDetailsData?.timeEntries?.entriesByLocation) return EMPTY_ARRAY;

        return timesheetDetailsData.timeEntries.entriesByLocation.map((locationTimeEntry) => ({
            locationName: locationTimeEntry.locationName,
            locationId: locationTimeEntry.locationId,
        }));
    }, [timesheetDetailsData?.timeEntries?.entriesByLocation]);

    const mappedTimeEntriesByLocation = useMemo(() => {
        if (timesheetDetailsLoading || !timesheetDetailsData?.timeEntries?.entriesByLocation)
            return [];

        return timesheetDetailsData.timeEntries.entriesByLocation.map((locationTimeEntry) => {
            const processedEntries = locationTimeEntry?.entries?.map((entry, index) => ({
                ...fillEmptyProcessedTimesheetEntriesValues(entry),
                date: formatUtcDate(entry.date, DATE_FORMATS.WEEKDAY_MD),
                id: entry?.id ?? `${entry.date}-${index}`,
                startTime: entry?.startTime
                    ? formatDate(entry.startTime, {
                          format: DATE_FORMATS.US_TIME,
                          tz: locationTimeEntry.timeZone,
                      })
                    : '-',
                endTime: entry?.endTime
                    ? formatDate(entry.endTime, {
                          format: DATE_FORMATS.US_TIME,
                          tz: locationTimeEntry.timeZone,
                      })
                    : '-',
                units:
                    entry?.units && entry?.units !== 0 && entry?.units !== '0' ? entry?.units : '-',
                hours:
                    entry?.hours && entry?.hours !== 0 && entry?.hours !== '0'
                        ? entry.hours.toFixed(2)
                        : '-',
                miles: formatIntegerOrDecimal(Number(entry.miles) === 0 ? null : entry.miles),
            }));

            const groupedEntries = formatRowsByRelatedEntryId(processedEntries);

            return {
                ...locationTimeEntry,
                entries: groupedEntries,
            };
        });
    }, [timesheetDetailsLoading, timesheetDetailsData?.timeEntries?.entriesByLocation]);

    const weekEndingStartDate = useMemo(
        () =>
            timesheetDetailsData?.weekEndingDate
                ? moment(timesheetDetailsData?.weekEndingDate).subtract(6, 'days')
                : null,
        [timesheetDetailsData?.weekEndingDate]
    );
    const weekEndingEndDate = useMemo(
        () =>
            timesheetDetailsData?.weekEndingDate
                ? moment(timesheetDetailsData?.weekEndingDate)
                : null,
        [timesheetDetailsData?.weekEndingDate]
    );

    const allLocationIds = useMemo(
        () => mappedTimeEntriesByLocation?.map(({ locationId }) => locationId) ?? [],
        [mappedTimeEntriesByLocation]
    );
    const [expandedLocations, setExpandedLocations] = useState([]);
    /* useEffect(() => {
        setExpandedLocations(allLocationIds); // expand all locations by default
    }, [allLocationIds]); */
    const handleChangeExpandLocation = (panel) => (event, expand) => {
        setExpandedLocations((prevState) =>
            expand ? [...prevState, panel] : prevState.filter((id) => id !== panel)
        );
    };
    const isAllLocationsExpanded = expandedLocations.length === allLocationIds.length;
    const handleExpandAllLocations = () => {
        setExpandedLocations(allLocationIds);
    };
    const handleCollapseAllLocations = () => {
        setExpandedLocations([]);
    };

    return (
        <PageContainer>
            {timesheetDetailsData?.isPaperTimesheet && (
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <PaperTimesheetBadge />
                </Stack>
            )}
            {timesheetDetailsData?.isCorrection && (
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <CorrectionsBadge />
                </Stack>
            )}
            <Grid container gap={{ xs: 2, md: 3, lg: 4 }}>
                <Grid size="grow">
                    <Stack gap={2}>
                        {renderVersionAlert()}
                        <LocationMismatchBanner timesheetDetails={timesheetDetailsData} />
                        <PartialInvoiceBanner
                            invoiceStatus={timesheetDetailsData?.invoiceStatus}
                            unInvoicedTotalToBill={
                                timesheetDetailsData?.totals?.unInvoicedTotalToBill
                            }
                            processedTimesheetEntries={
                                timesheetDetailsData?.processedTimesheetEntries
                            }
                        />
                        <ProcessedTimesheetHeader
                            data={timesheetDetailsData}
                            className={`${classes.box} ${classes.noPadding}`}
                            loading={timesheetDetailsLoading || timesheetDetailsFetching}
                            readOnly={!canEditHeaderDate}
                            locations={locations}
                        />

                        <Stack
                            className={`${classes.box} ${classes.totalContainer}`}
                            height={{ xs: 'auto', sm: '28px' }}
                            direction={{ xs: 'column', sm: 'row' }}
                            gap={{ xs: 0, sm: 2 }}
                            justifyContent="space-evenly"
                            alignItems="center"
                            divider={<Divider orientation="vertical" flexItem />}
                        >
                            <Typography className={classes.total}>
                                Total to Bill:
                                {timesheetDetailsLoading ? (
                                    <Skeleton
                                        variant="text"
                                        sx={{
                                            fontSize: 16,
                                            width: 55,
                                            display: 'inline-block',
                                        }}
                                    />
                                ) : (
                                    <span>
                                        {currencyFormatter(
                                            timesheetDetailsData?.totals?.totalToBill || 0
                                        )}
                                    </span>
                                )}
                            </Typography>
                            <Typography className={classes.total}>
                                Total to Pay:
                                {timesheetDetailsLoading ? (
                                    <Skeleton
                                        variant="text"
                                        sx={{
                                            fontSize: 16,
                                            width: 55,
                                            display: 'inline-block',
                                        }}
                                    />
                                ) : (
                                    <span>
                                        {currencyFormatter(
                                            timesheetDetailsData?.totals?.totalToPay || 0
                                        )}
                                    </span>
                                )}
                            </Typography>
                            <Typography className={classes.total}>
                                Total Malpractice:
                                {timesheetDetailsLoading ? (
                                    <Skeleton
                                        variant="text"
                                        sx={{
                                            fontSize: 16,
                                            width: 55,
                                            display: 'inline-block',
                                        }}
                                    />
                                ) : (
                                    <span>
                                        {currencyFormatter(
                                            timesheetDetailsData?.totals?.totalMalpractice || 0
                                        )}
                                    </span>
                                )}
                            </Typography>
                            <Typography className={classes.total}>
                                Total Cost:
                                {timesheetDetailsLoading ? (
                                    <Skeleton
                                        variant="text"
                                        sx={{
                                            fontSize: 16,
                                            width: 55,
                                            display: 'inline-block',
                                        }}
                                    />
                                ) : (
                                    <span>
                                        {currencyFormatter(
                                            timesheetDetailsData?.totals?.totalCost || 0
                                        )}
                                    </span>
                                )}
                            </Typography>
                        </Stack>

                        <Grid className={classes.box}>
                            {timesheetDetailsLoading && (
                                <>
                                    <Skeleton sx={{ fontSize: 16, width: 270 }} />
                                    <Skeleton sx={{ fontSize: 12, width: 290, mt: 0.5, mb: 2 }} />
                                    <Accordion variant="lt-admin-info" expanded>
                                        <AccordionSummary sx={{ py: '4px !important' }}>
                                            <Stack sx={{ width: 300 }}>
                                                <Skeleton sx={{ fontSize: 20 }} />
                                                <Skeleton sx={{ fontSize: 13 }} />
                                            </Stack>
                                            <Skeleton sx={{ height: 40, width: 350, ml: 'auto' }} />
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <StripedDataGrid
                                                isLoading
                                                columns={processedTimesheetEntriesTableColumns}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                </>
                            )}
                            {!timesheetDetailsLoading && timesheetDetailsData?.timeEntries && (
                                <>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            fontSize={16}
                                            lineHeight="120%"
                                            color={COLORS.LT_MIDNIGHT}
                                        >
                                            Week of{' '}
                                            {formatUtcDate(
                                                weekEndingStartDate?.format('YYYY-MM-DD'),
                                                DATE_FORMATS.SHORT_DATE
                                            )}
                                            {' - '}
                                            {formatUtcDate(
                                                timesheetDetailsData.weekEndingDate,
                                                DATE_FORMATS.SHORT_DATE
                                            )}
                                        </Typography>
                                        {allLocationIds.length > 1 && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={
                                                    isAllLocationsExpanded
                                                        ? handleCollapseAllLocations
                                                        : handleExpandAllLocations
                                                }
                                                sx={{ ml: 2, mr: 'auto' }}
                                            >
                                                {isAllLocationsExpanded
                                                    ? 'Collapse all'
                                                    : 'Expand all'}{' '}
                                                <ExpandMoreIcon
                                                    fontSize="small"
                                                    sx={{
                                                        transform: isAllLocationsExpanded
                                                            ? 'rotate(180deg)'
                                                            : 'rotate(0deg)',
                                                    }}
                                                />
                                            </Button>
                                        )}
                                        <Stack direction="row" gap={2}>
                                            {!isLTAssociateUser &&
                                                ltoeApiTimesheetEnableEditApprovedTimesheet && (
                                                    <Button
                                                        component={Link}
                                                        to={
                                                            canEditRawTimesheet
                                                                ? AdminTableConfig.getEditUrl({
                                                                      id: timesheetDetailsData.rawTimesheetId,
                                                                      clinicianId:
                                                                          timesheetDetailsData.clinicianId,
                                                                      weekEnding:
                                                                          timesheetDetailsData.weekEndingDate.split(
                                                                              'T'
                                                                          )[0],
                                                                  })
                                                                : AdminTableConfig.getTimeEntryUrl({
                                                                      id: timesheetDetailsData.rawTimesheetId,
                                                                      clinicianId:
                                                                          timesheetDetailsData.clinicianId,
                                                                      weekEnding:
                                                                          timesheetDetailsData.weekEndingDate.split(
                                                                              'T'
                                                                          )[0],
                                                                  })
                                                        }
                                                        target="_blank"
                                                    >
                                                        <EditOutlinedIcon
                                                            sx={{ fontSize: 20, mr: 0.5 }}
                                                        />
                                                        <Typography sx={{ fontSize: 12 }}>
                                                            {canEditRawTimesheet ? 'Edit' : 'View'}{' '}
                                                            Clinician Timesheet
                                                        </Typography>
                                                    </Button>
                                                )}
                                            <DownloadTimesheetsButton
                                                entries={
                                                    timesheetDetailsData?.timeEntries
                                                        ?.entriesByLocation ?? []
                                                }
                                                weekEndingDate={formatUtcDate(
                                                    timesheetDetailsData?.weekEndingDate,
                                                    DATE_FORMATS.LT
                                                )}
                                                clinicianId={clinicianId}
                                                clinicianName={clinicianName}
                                                rawTimesheetId={rawTimesheetId}
                                                timesheetDetailsData={timesheetDetailsData}
                                            />
                                        </Stack>
                                    </Stack>

                                    <Stack
                                        direction="row"
                                        gap="12px"
                                        divider={<Divider orientation="vertical" flexItem />}
                                        mt={1}
                                        mb="20px"
                                    >
                                        <Typography className={classes.timeEntryHeaderMetrics}>
                                            Total Hours:{' '}
                                            {timesheetDetailsData.timeEntries?.totalHours?.toFixed(
                                                2
                                            )}
                                        </Typography>
                                        <Typography className={classes.timeEntryHeaderMetrics}>
                                            Total Units:{' '}
                                            {timesheetDetailsData.timeEntries?.totalUnits?.toFixed(
                                                2
                                            )}
                                        </Typography>
                                        <Typography className={classes.timeEntryHeaderMetrics}>
                                            Total Miles:{' '}
                                            {timesheetDetailsData.timeEntries?.totalMiles?.toFixed(
                                                2
                                            )}
                                        </Typography>
                                    </Stack>

                                    <Stack gap="20px">
                                        {mappedTimeEntriesByLocation?.map((locationTimeEntry) => {
                                            const approverLabel = formatTimesheetApproverLabel(
                                                locationTimeEntry.approver
                                            );

                                            return (
                                                <Accordion
                                                    variant="lt-admin-info"
                                                    key={locationTimeEntry.locationId}
                                                    expanded={expandedLocations.includes(
                                                        locationTimeEntry.locationId
                                                    )}
                                                    onChange={handleChangeExpandLocation(
                                                        locationTimeEntry.locationId
                                                    )}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={
                                                            <ExpandMoreIcon fontSize="small" />
                                                        }
                                                    >
                                                        <Stack>
                                                            <Typography
                                                                fontSize="16px"
                                                                lineHeight="120%"
                                                            >
                                                                {locationTimeEntry.locationName}
                                                            </Typography>
                                                            {approverLabel && (
                                                                <Typography fontSize="13px">
                                                                    {approverLabel}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            gap="30px"
                                                            ml="auto"
                                                            mr="30px"
                                                        >
                                                            <Typography
                                                                className={classes.timeEntryMetrics}
                                                            >
                                                                <span>Hours:</span>
                                                                {`${formatIntegerOrDecimal(
                                                                    locationTimeEntry.totalHours.toFixed(
                                                                        2
                                                                    )
                                                                )} ${pluralize(
                                                                    locationTimeEntry.totalHours,
                                                                    'Hour'
                                                                )}`}
                                                            </Typography>
                                                            <Typography
                                                                className={classes.timeEntryMetrics}
                                                            >
                                                                <span>Units:</span>
                                                                {`${
                                                                    locationTimeEntry.totalUnits
                                                                } ${pluralize(
                                                                    locationTimeEntry.totalUnits,
                                                                    'Unit'
                                                                )}`}
                                                            </Typography>
                                                            <Typography
                                                                className={classes.timeEntryMetrics}
                                                            >
                                                                <span>Miles:</span>
                                                                {`${formatIntegerOrDecimal(
                                                                    locationTimeEntry.totalMiles.toFixed(
                                                                        2
                                                                    )
                                                                )} ${pluralize(
                                                                    locationTimeEntry.totalMiles,
                                                                    'Mile'
                                                                )}`}
                                                            </Typography>
                                                        </Stack>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <StripedDataGrid
                                                            columns={
                                                                processedTimesheetEntriesTableColumns
                                                            }
                                                            rows={locationTimeEntry.entries}
                                                            showOptions={false}
                                                            groupedBy="relatedEntryId"
                                                            isLoading={timesheetDetailsLoading}
                                                        />
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Stack>
                                </>
                            )}
                            {!timesheetDetailsLoading && !timesheetDetailsData?.timeEntries && (
                                <Typography>No time entries found</Typography>
                            )}
                        </Grid>

                        <Grid className={classes.box}>
                            <ProcessedTimesheetDetailsTable
                                isLoading={timesheetDetailsLoading}
                                processedTimesheetId={processedTimesheetId}
                                data={timesheetDetailsData?.processedTimesheetEntries || []}
                                availableRates={timesheetDetailsData?.availableRates || []}
                                weekEndingStartDate={weekEndingStartDate}
                                weekEndingEndDate={weekEndingEndDate}
                                initialSortModel={[{ field: 'date', sort: 'asc' }]}
                                refetch={refetchProcessedTimesheetDetails}
                                locations={locations}
                                locationsPTSIds={timesheetDetailsData?.locationPTSIds ?? []}
                                readOnly={readOnly}
                                splitByWorkLocation={timesheetDetailsData?.splitByWorkLocation}
                            />
                        </Grid>
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                    <Stack gap={3}>
                        <ReviewReasonsAccordion
                            reviewReasons={timesheetDetailsData?.reasons}
                            exceptionNotes={timesheetDetailsData?.exceptionNotes}
                            loading={timesheetDetailsLoading}
                        />

                        {!timesheetDetailsLoading && showCorrectionCard && (
                            <Box className={classes.box}>
                                <CorrectionCard
                                    id={processedTimesheetId}
                                    status={timesheetDetailsData?.status}
                                />
                            </Box>
                        )}

                        <Grid size="grow" className={classes.box}>
                            <Stack gap={3} divider={<Divider sx={{ borderBottom: 'none' }} />}>
                                {!isLTAssociateUser && (
                                    <Stack gap={1}>
                                        <Typography sx={{ color: COLORS.LT_JOURNEY175 }}>
                                            Add a comment
                                        </Typography>
                                        <RichTextEditor
                                            placeholder="Add a comment"
                                            onSubmit={handleNewComment}
                                            loading={isPostingComment}
                                            apiError={postCommentError}
                                            ref={rteRef}
                                        />
                                    </Stack>
                                )}
                                <ActivityFeed
                                    data={activityFeedData}
                                    error={
                                        isActivityFeedError
                                            ? 'Activity feed could not be updated. Please try again.'
                                            : null
                                    }
                                    loading={activityFeedLoading}
                                />
                            </Stack>
                        </Grid>

                        <Stack className={classes.box} gap={2}>
                            <Attachments
                                processedTimesheetId={processedTimesheetId}
                                lastModified={timesheetDetailsData?.lastModified}
                                rawTimesheetId={rawTimesheetId}
                                locations={locations}
                                readOnly={!canEditAttachment}
                            />
                        </Stack>

                        <Box className={classes.box}>
                            <AssociatedInvoiceDocuments
                                processedTimesheetId={processedTimesheetId}
                            />
                        </Box>

                        <Stack direction="row" justifyContent="flex-end">
                            <ProcessedTimesheetHistory
                                processedTimesheetId={processedTimesheetId}
                                locations={locations}
                                availableRates={timesheetDetailsData?.availableRates || EMPTY_ARRAY}
                                entries={
                                    timesheetDetailsData?.processedTimesheetEntries || EMPTY_ARRAY
                                }
                            />
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </PageContainer>
    );
};

const ProcessedTimesheetWithContext = ({ classes }) => (
    <ProcessedTimesheetContextProvider>
        <ProcessedTimesheetDetails classes={classes} />
    </ProcessedTimesheetContextProvider>
);

export default withStyles(styles)(ProcessedTimesheetWithContext);
